import Cookies from 'js-cookie';
import { getCurrentAddress } from '../helpers/utilities';

/**
 * Helper function to generate the header for POST requests
 * @returns object with credentials
 */
export function generateHeader() {
  const userId = Cookies.get('user_id');
  const token = Cookies.get('token');

  const headers = {};
  if (token) {
    headers.authorization = token;
  }
  if (userId) {
    headers.user_id = userId;
  }
  return headers;
}

/**
 * Retrieve results from the API using a GET request
 * @param {*} targetUrl 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
/**
 * Retrieve results from the API using a GET request with credentials
 * @param {*} targetUrl 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function getData(targetUrl, onSuccess, onError) {
  const url = getCurrentAddress();
  await fetch(url + targetUrl, {
    credentials: "include",
  })
    .then((response) => response.json())
    .then(onSuccess)
    .catch(onError);
}

/**
 * Upload data to the API with a POST request
 * @param {*} targetUrl 
 * @param {*} data 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function postData(targetUrl, data, onSuccess, onError) {
  const url = getCurrentAddress();
  await fetch(url + targetUrl, {
    method: "POST",
    headers: {
      "Content-type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  })
    .then((response) => response.json())
    .then(onSuccess)
    .catch(onError);
}

/**
 * Upload data to the API with a header in a POST request
 * @param {*} targetUrl 
 * @param {*} data 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function postDataWithHeader(targetUrl, data, onSuccess, onError) {
  const url = getCurrentAddress();
  await fetch(url + targetUrl, {
    method: "POST",
    headers: {
      "Content-type": "application/json",
      ...generateHeader(),
    },
    credentials: "include",
    body: JSON.stringify(data),
  })
    .then((response) => response.json())
    .then(onSuccess)
    .catch(onError);
}

/**
 * Upload file to the targeted API with a POST request
 * @param {*} targetUrl 
 * @param {*} data 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function uploadFile(targetUrl, data, onSuccess, onError) {
  const url = getCurrentAddress();

  var formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value);
  });

  await fetch(url + targetUrl, {
    method: "POST",
    headers: generateHeader(),
    credentials: "include",
    body: formData,
  })
    .then((response) => response.json())
    .then(onSuccess)
    .catch(onError);
}

/**
 * getData helper to implement onSuccess and onError message
 * Reduce the need to implement onErrror everytime
 * @param {*} targetUrl 
 * @param {*} onTriggerMessage 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function getDataHelper(targetUrl, onTriggerMessage, onSuccess, onError) {

  const onErrorImpl = (e) => {
    if (onError != undefined) {
      onError(e);
    } else {
      onTriggerMessage(
        "Failed to retrieve data",
        `Error from fetching ${targetUrl}: Error = ${e}`,
        "danger"
      );
    }
  };

  const onSuccessImpl = (data) => {
    if (data.status === 3) {
      onSuccess(data);
    } else {
      onErrorImpl(data);
    }
  };

  await getData(targetUrl, onSuccessImpl, onErrorImpl);
}

/**
 * uploadFile helper to implement onSuccess and onError message
 * Reduce the need to implement onErrror everytime
 * @param {*} targetUrl 
 * @param {*} inputData 
 * @param {*} onTriggerMessage 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function uploadFileHelper(targetUrl, inputData, onTriggerMessage, onSuccess, onError) {

  const onErrorImpl = (e) => {
    if (onError != undefined) {
      onError(e);
    } else {
      onTriggerMessage(
        "Failed to retrieve data",
        `Error from fetching ${targetUrl}: Error = ${e}`,
        "danger"
      );
    }
  };

  const onSuccessImpl = (data) => {
    if (data.status === 3) {
      onSuccess(data);
    } else {
      onErrorImpl(data);
    }
  };

  await uploadFile(targetUrl, inputData, onSuccessImpl, onErrorImpl);
}

/**
 * postData helper to implement onSuccess and onError message
 * Reduce the need to implement onErrror everytime
 * @param {*} targetUrl 
 * @param {*} inputData 
 * @param {*} onTriggerMessage 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function postDataHelper(targetUrl, inputData, onTriggerMessage, onSuccess, onError) {
  const onErrorImpl = (e) => {
    if (onError != undefined) {
      onError(e);
    } else {
      onTriggerMessage(
        "Failed to retrieve data",
        `Error from fetching ${targetUrl}: Error = ${e}`,
        "danger"
      );
    }
  };

  const onSuccessImpl = (data) => {
    if (data.status === 3) {
      onSuccess(data);
    } else {
      onErrorImpl(data);
    }
  };

  await postData(targetUrl, inputData, onSuccessImpl, onErrorImpl);
}

/**
 * postDataWithHeader helper to implement onSuccess and onError message
 * Reduce the need to implement onErrror everytime
 * @param {*} targetUrl 
 * @param {*} inputData 
 * @param {*} onTriggerMessage 
 * @param {*} onSuccess 
 * @param {*} onError 
 */
export async function postDataWithHeaderHelper(targetUrl, inputData, onTriggerMessage, onSuccess, onError) {
  const onErrorImpl = (e) => {
    if (onError != undefined) {
      onError(e);
    } else {
      onTriggerMessage(
        "Failed to retrieve data",
        `Error from fetching ${targetUrl}: Error = ${e}`,
        "danger"
      );
    }
  };

  const onSuccessImpl = (data) => {
    if (data.status === 3) {
      onSuccess(data);
    } else {
      onErrorImpl(data);
    }
  };

  await postDataWithHeader(targetUrl, inputData, onSuccessImpl, onErrorImpl);
}
